import { useState } from 'react';
import { Link } from 'react-router-dom';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/feature/Reveal';
import { GET_EA_FAQ } from '@/pages/get-ea/data';

/**
 * A compact objection-handling FAQ for the Get EA page.
 *
 * These four questions are the ones a visitor hesitates on right before they
 * open the chat — is it really free, will it run on my setup, is my balance
 * enough, and does a free EA secretly use martingale or grid. The questions and
 * answers come from the shared get-ea data module, so the page's FAQPage
 * structured data describes exactly the same content that is rendered here.
 */
export default function MiniFaq() {
  const [openId, setOpenId] = useState<string>(GET_EA_FAQ[0].id);

  const toggle = (id: string) => {
    setOpenId((prev) => (prev === id ? '' : id));
  };

  return (
    <section className="py-14 md:py-20 bg-background-100 border-y border-background-200">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-shell mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="Common questions"
              title="The things everyone asks before claiming"
              subtitle="Short, honest answers — no fine print hiding behind the word free."
            />
          </Reveal>

          <Reveal delay={80}>
            <div className="mt-10 md:mt-12 max-w-3xl mx-auto flex flex-col gap-3">
              {GET_EA_FAQ.map((item) => {
                const open = openId === item.id;
                const panelId = `get-ea-faq-${item.id}`;
                return (
                  <div
                    key={item.id}
                    className={`rounded-lg border transition-colors duration-200 ${
                      open
                        ? 'border-accent-300 bg-accent-50'
                        : 'border-background-200 bg-background-50 hover:border-background-300'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(item.id)}
                      aria-expanded={open}
                      aria-controls={panelId}
                      className="w-full flex items-center justify-between gap-4 px-5 py-4 md:py-5 text-left cursor-pointer"
                    >
                      <span className="font-heading text-sm md:text-base font-semibold text-foreground-950">
                        {item.question}
                      </span>
                      <span
                        className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-all duration-300 ${
                          open ? 'bg-accent-500 text-primary-950 rotate-180' : 'bg-background-200 text-foreground-700'
                        }`}
                      >
                        <i className="ri-arrow-down-s-line text-lg" aria-hidden="true"></i>
                      </span>
                    </button>
                    <div
                      id={panelId}
                      role="region"
                      aria-label={item.question}
                      className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-[420px]' : 'max-h-0'}`}
                    >
                      <p className="px-5 pb-5 text-xs md:text-sm leading-relaxed text-foreground-600">{item.answer}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="mt-6 flex justify-center">
              <Link
                to="/faq"
                className="inline-flex items-center gap-1.5 text-xs md:text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Read every question on the FAQ page
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}